import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import {socialMediaGridExtraReducers} from '@Components/social-media/social-media-extra-reducers';
import type {SocialPost} from '@Components/social-media/post.vo';
import {PostState} from '@Components/social-media/post.vo';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {AccountState, doesUsableAccountNeedReconnection, RECONNECT_REQUIRED_ACCOUNT_STATES} from '@Components/social-media/account.vo';
import type {AccountStateFilter, PostStateFilter, RecentDesignForPublishing} from '@Components/social-media/social-media.types';
import {AccountSortingMetric, PostSortingMetric} from '@Components/social-media/social-media.types';

export interface SocialMediaGridsState {
  posts: SocialPost[];
  postStateFilter: PostStateFilter;
  getDeletedPosts: boolean;
  getSharedInTeamPosts: boolean;
  postSorter: PostSortingMetric;
  havePostsLoaded: boolean;
  doRefetchGridForNewSorting: boolean;
  morePostsOnServer: boolean;
  accounts: SocialAccount[];
  accountStateFilter: AccountStateFilter;
  accountSorter: AccountSortingMetric;
  haveAccountsLoaded: boolean;
  recentDesignsForPublishing: RecentDesignForPublishing[];
  haveRecentDesignsForPublishingLoaded: boolean;
  searchTerm: string;
  /** Set of post IDs whose metrics are being auto-refreshed (drives the skeleton). Kept as a map for O(1) per-item lookups during polling. */
  refreshingPostIds: Record<string, true>;
  /** Set of account IDs currently being reconnected (drives the reconnect skeleton). Kept as a map for O(1) per-item lookups. */
  reconnectingAccountIds: Record<number, true>;
  /** Set of account IDs whose failed slots are currently being retried, so concurrent "Fix this" retries on one post can't race the publish-claim lock. */
  retryingAccountIds: Record<number, true>;
  isAttentionBannerDismissed: boolean;
  /** True while the reconnect-only status filter was armed by the attention banner (not chosen by the user), so the batch stays pinned and the filter auto-releases when it's done. */
  reconnectFilterArmed: boolean;
  /**
   * The accounts that needed reconnecting when the banner armed the filter. Every one stays visible for
   * the life of the batch, so a fixed row turns healthy in place instead of vanishing — including when a
   * single reconnect heals several accounts at once (re-authing a Facebook profile also revives its pages).
   */
  armedReconnectBatchAccountIds: Record<number, boolean>;
}

export const getAccountsInitialFilter = (): AccountStateFilter => {
  const accountFilters: string[] = Object.values(AccountState);
  const initialFilter: Record<string, boolean> = {};

  accountFilters.forEach((accountFilter) => {
    initialFilter[accountFilter] = true;
  });

  return initialFilter as AccountStateFilter;
};

const getPostsInitialFilter = (): PostStateFilter => {
  const postStates: string[] = Object.values(PostState);
  const initialFilter: Record<string, boolean> = {};

  postStates.forEach((postState) => {
    initialFilter[postState] = true;
  });

  return initialFilter as PostStateFilter;
};

export const DEFAULT_POSTS_LIMIT = 30;

const initialState: SocialMediaGridsState = {
  posts: [],
  postStateFilter: getPostsInitialFilter(),
  getDeletedPosts: false,
  getSharedInTeamPosts: false,
  postSorter: PostSortingMetric.DATE_PUBLISHED,
  havePostsLoaded: false,
  doRefetchGridForNewSorting: false,
  morePostsOnServer: true,
  accounts: [],
  accountStateFilter: getAccountsInitialFilter(),
  accountSorter: AccountSortingMetric.DATE_CREATED,
  haveAccountsLoaded: false,
  recentDesignsForPublishing: [],
  haveRecentDesignsForPublishingLoaded: false,
  searchTerm: '',
  refreshingPostIds: {},
  reconnectingAccountIds: {},
  retryingAccountIds: {},
  isAttentionBannerDismissed: false,
  reconnectFilterArmed: false,
  armedReconnectBatchAccountIds: {},
};

export const socialMediaGridsSlice = createSlice({
  name: 'socialMediaGrid',
  initialState,
  reducers: {
    updatePostFilter: (state, {payload}: PayloadAction<PostStateFilter>) => {
      doUpdatePostFilter(state, payload);
    },
    resetPostsGrid: (state) => {
      state.posts = initialState.posts;
      state.postStateFilter = initialState.postStateFilter;
      state.postSorter = initialState.postSorter;
      state.havePostsLoaded = initialState.havePostsLoaded;
      state.morePostsOnServer = initialState.morePostsOnServer;
      state.refreshingPostIds = {};
    },
    resetPosts: (state) => {
      state.posts = initialState.posts;
      state.havePostsLoaded = initialState.havePostsLoaded;
      state.refreshingPostIds = {};
    },
    updateGetDeletedPosts: (state, {payload}: PayloadAction<boolean>) => {
      doUpdateGetDeletedPosts(state, payload);
    },
    updateGetSharedInTeamPosts: (state, {payload}: PayloadAction<boolean>) => {
      doUpdateGetSharedInTeamPosts(state, payload);
    },
    updatePostSorter: (state, {payload}: PayloadAction<PostSortingMetric>) => {
      doUpdatePostSorter(state, payload);
    },
    updateAccountSorter: (state, {payload}: PayloadAction<AccountSortingMetric>) => {
      doUpdateAccountSorter(state, payload);
    },
    updateHavePostsLoaded: (state, {payload}: PayloadAction<boolean>) => {
      updatePostsHaveLoaded(state, payload);
    },
    updateDoRefetchGridForNewSorting: (state, {payload}: PayloadAction<boolean>) => {
      doUpdateDoRefetchGridForNewSorting(state, payload);
    },
    deleteSocialAccount: (state, {payload}: PayloadAction<number>) => {
      doDeleteSocialAccount(state, payload);
    },
    deleteBulkSocialAccounts: (state, {payload}: PayloadAction<number[]>) => {
      doDeleteBulkSocialAccounts(state, payload);
    },
    deleteSocialPost: (state, {payload}: PayloadAction<string>) => {
      doDeleteSocialPost(state, payload);
    },
    updateAccountsFilter: (state, {payload}: PayloadAction<AccountStateFilter>) => {
      doUpdateAccountFilter(state, payload);
    },
    updateSearchTerm: (state, {payload}: PayloadAction<string>) => {
      doUpdateSearchTerm(state, payload);
    },

    addSocialPostToGrid: (state, {payload}: PayloadAction<SocialPost>) => {
      doAddSocialPostToGrid(state, payload);
    },

    setPosts: (state, {payload}: PayloadAction<SocialPost[]>): void => {
      updateSocialPosts(state, payload);
    },

    markPostsAsRefreshing: (state, {payload}: PayloadAction<string[]>): void => {
      doMarkPostsAsRefreshing(state, payload);
    },

    unmarkPostsAsRefreshing: (state, {payload}: PayloadAction<string[]>): void => {
      doUnmarkPostsAsRefreshing(state, payload);
    },

    mergeRefreshedPosts: (state, {payload}: PayloadAction<SocialPost[]>): void => {
      doMergeRefreshedPosts(state, payload);
    },

    markAccountsReconnecting: (state, {payload}: PayloadAction<number[]>): void => {
      addAccountIds(state.reconnectingAccountIds, payload);
    },

    unmarkAccountsReconnecting: (state, {payload}: PayloadAction<number[]>): void => {
      removeAccountIds(state.reconnectingAccountIds, payload);
    },

    markAccountsRetrying: (state, {payload}: PayloadAction<number[]>): void => {
      addAccountIds(state.retryingAccountIds, payload);
    },

    unmarkAccountsRetrying: (state, {payload}: PayloadAction<number[]>): void => {
      removeAccountIds(state.retryingAccountIds, payload);
    },

    dismissAttentionBanner: (state): void => {
      state.isAttentionBannerDismissed = true;
    },

    armReconnectAccountsFilter: (state): void => {
      doArmReconnectAccountsFilter(state);
    },

    releaseReconnectFilter: (state): void => {
      doReleaseReconnectFilter(state);
    },
  },
  extraReducers: socialMediaGridExtraReducers,
});

const doMarkPostsAsRefreshing = (state: SocialMediaGridsState, postIds: string[]): void => {
  postIds.forEach((id) => {
    state.refreshingPostIds[id] = true;
  });
};

const doUnmarkPostsAsRefreshing = (state: SocialMediaGridsState, postIds: string[]): void => {
  const toRemove = new Set(postIds);
  state.refreshingPostIds = Object.fromEntries(Object.entries(state.refreshingPostIds).filter(([id]) => !toRemove.has(id)));
};

const addAccountIds = (accountIdMap: Record<number, true>, accountIds: number[]): void => {
  accountIds.forEach((id) => {
    accountIdMap[id] = true;
  });
};

const removeAccountIds = (accountIdMap: Record<number, true>, accountIds: number[]): void => {
  accountIds.forEach((id) => {
    delete accountIdMap[id];
  });
};

/**
 * Swaps in refreshed post objects by id. Untouched posts keep their existing
 * reference (only refreshed rows get a new one), so memoized grid items that
 * didn't change don't re-render.
 */
const doMergeRefreshedPosts = (state: SocialMediaGridsState, freshPosts: SocialPost[]): void => {
  const freshById = new Map(freshPosts.map((post) => [post.id, post]));
  state.posts.forEach((post, index) => {
    const fresh = freshById.get(post.id);
    if (fresh) {
      state.posts[index] = fresh;
    }
  });
};

const doAddSocialPostToGrid = (state: SocialMediaGridsState, socialPost: SocialPost): void => {
  const tempPosts = state.posts;
  tempPosts.push(socialPost);
  state.posts = tempPosts;
};
const doUpdateSearchTerm = (state: SocialMediaGridsState, updatedSearchTerm: string): void => {
  state.searchTerm = updatedSearchTerm;
};
const doUpdateGetDeletedPosts = (state: SocialMediaGridsState, getDeletedPosts: boolean): void => {
  state.getDeletedPosts = getDeletedPosts;
};

const doUpdateGetSharedInTeamPosts = (state: SocialMediaGridsState, getSharedInTeamPosts: boolean): void => {
  state.getSharedInTeamPosts = getSharedInTeamPosts;
};

const doDeleteSocialAccount = (state: SocialMediaGridsState, idAccount: number): void => {
  state.accounts = state.accounts.filter((account) => {
    return account.id !== idAccount;
  });
};
const doDeleteSocialPost = (state: SocialMediaGridsState, idPost: string): void => {
  state.posts = state.posts.filter((post) => {
    return post.id !== idPost;
  });
};
const doDeleteBulkSocialAccounts = (state: SocialMediaGridsState, idAccounts: number[]): void => {
  state.accounts = state.accounts.filter((account) => {
    return !idAccounts.includes(account.id);
  });
};
const doUpdateAccountSorter = (state: SocialMediaGridsState, payload: AccountSortingMetric): void => {
  state.accountSorter = payload;
};
const doUpdatePostSorter = (state: SocialMediaGridsState, payload: PostSortingMetric): void => {
  state.postSorter = payload;
};
const doUpdateAccountFilter = (state: SocialMediaGridsState, payload: AccountStateFilter): void => {
  state.accountStateFilter = payload;
  // A manual filter change means the user has taken control, so a later fix must not disturb their filter.
  state.reconnectFilterArmed = false;
  state.armedReconnectBatchAccountIds = {};
};

/** Reconnect-only account filter: everything off except the states that mean "needs reconnecting". */
const getReconnectOnlyAccountsFilter = (): AccountStateFilter => {
  const filter: Record<string, boolean> = {};

  Object.values(AccountState).forEach((accountState) => {
    filter[accountState] = false;
  });

  RECONNECT_REQUIRED_ACCOUNT_STATES.forEach((accountState) => {
    filter[accountState] = true;
  });

  return filter as AccountStateFilter;
};

const doArmReconnectAccountsFilter = (state: SocialMediaGridsState): void => {
  state.accountStateFilter = getReconnectOnlyAccountsFilter();
  state.reconnectFilterArmed = true;
  state.armedReconnectBatchAccountIds = {};
  state.accounts.forEach((account) => {
    if (doesUsableAccountNeedReconnection(account)) {
      state.armedReconnectBatchAccountIds[account.id] = true;
    }
  });
};

/** Once the batch is worked through, drop the reconnect-only filter back to show-all and forget the batch. */
const doReleaseReconnectFilter = (state: SocialMediaGridsState): void => {
  state.accountStateFilter = getAccountsInitialFilter();
  state.reconnectFilterArmed = false;
  state.armedReconnectBatchAccountIds = {};
};
const doUpdatePostFilter = (state: SocialMediaGridsState, payload: PostStateFilter): void => {
  state.postStateFilter = payload;
};

export const doUpdateDoRefetchGridForNewSorting = (state: SocialMediaGridsState, payload: boolean): void => {
  state.doRefetchGridForNewSorting = payload;
};

export const updateMorePostsOnServer = (state: SocialMediaGridsState, payload: boolean): void => {
  state.morePostsOnServer = payload;
};
export const updateSocialPosts = (state: SocialMediaGridsState, payload: SocialPost[]): void => {
  state.posts = payload;
};

export const updateAccountsHaveLoaded = (state: SocialMediaGridsState, payload: boolean): void => {
  state.haveAccountsLoaded = payload;
};

export const updatePostsHaveLoaded = (state: SocialMediaGridsState, payload: boolean): void => {
  state.havePostsLoaded = payload;
};

export const updateSocialAccounts = (state: SocialMediaGridsState, payload: SocialAccount[]): void => {
  state.accounts = payload;
};

export const updateRecentDesignsForPublishingHaveLoaded = (state: SocialMediaGridsState, payload: boolean): void => {
  state.haveRecentDesignsForPublishingLoaded = payload;
};

export const updateRecentDesignsForPublishing = (state: SocialMediaGridsState, payload: RecentDesignForPublishing[]): void => {
  state.recentDesignsForPublishing = payload;
};

export const {
  resetPostsGrid,
  resetPosts,
  updateGetDeletedPosts,
  updateGetSharedInTeamPosts,
  updateAccountsFilter,
  deleteSocialAccount,
  deleteBulkSocialAccounts,
  deleteSocialPost,
  updatePostSorter,
  updateAccountSorter,
  updatePostFilter,
  updateHavePostsLoaded,
  updateSearchTerm,
  updateDoRefetchGridForNewSorting,
  addSocialPostToGrid,
  setPosts,
  markPostsAsRefreshing,
  unmarkPostsAsRefreshing,
  mergeRefreshedPosts,
  markAccountsReconnecting,
  unmarkAccountsReconnecting,
  markAccountsRetrying,
  unmarkAccountsRetrying,
  dismissAttentionBanner,
  armReconnectAccountsFilter,
  releaseReconnectFilter,
} = socialMediaGridsSlice.actions;

export const socialMediaGridsReducer = socialMediaGridsSlice.reducer;
